import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-hot-toast';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccordionActions from '@mui/material/AccordionActions';
import useGetUserRole from '../hooks/useGetUserRole';
import usePermissions from '../hooks/usePermissions';
import Modal from './Modal';
import Spinner from './spinner/Spinner';
import ModuleFAQForm from './ModuleFAQForm';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

function ModuleFAQs({ moduleName }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [selectedFaq, setSelectedFaq] = useState(null);
  const permissions = usePermissions();
  const role = useGetUserRole();
  const { moduleId } = useParams();
  const queryClient = useQueryClient();

  const onEditFAQBtnClickHandler = (faq) => {
    setSelectedFaq(faq);
    setModalOpen(!modalOpen);
  };

  const onDeleteFAQBtnClickHandler = (faq) => {
    setSelectedFaq(faq);
    setDeleteModalOpen(!deleteModalOpen);
  };

  const onCloseFAQModalHandler = () => {
    setModalOpen(false);
    setSelectedFaq(null);
  };

  const fetchModuleFAQs = async () => {
    if (!moduleId) {
      return [];
    }
    const { data: response } = await axiosInstancev2.get('/module-faqs', {
      params: { module_id: moduleId },
    });
    if (response.error) {
      toast.error(response.error || 'Failed to load FAQs');
      return [];
    }
    return response.faqs || [];
  };

  const { data: moduleFAQs, isLoading } = useQuery(
    ['module-faqs', moduleId],
    fetchModuleFAQs,
    {
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      enabled: Boolean(moduleId),
    }
  );

  const { mutate, isLoading: deletingFAQ } = useMutation(
    async (faqId) => {
      return axiosInstancev2.delete(`/module-faqs/${faqId}`);
    },
    {
      onSuccess: (response) => {
        const { res } = response.data;
        if (res?.error) {
          toast.error(res.error || 'Failed to remove FAQ');
        } else {
          queryClient.invalidateQueries(['module-faqs', moduleId]);
          toast.success('FAQ successfully removed');
          setDeleteModalOpen(!deleteModalOpen);
        }
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to remove FAQ. Try again.';
        toast.error(msg);
      },
    }
  );

  const onDeleteFAQClickHandler = () => {
    if (!selectedFaq?.id) return;
    mutate(selectedFaq.id);
  };

  return (
    <div className="space-y-2">
      <div className=" flex justify-between items-center pb-2">
        <h4>{moduleName} FAQs</h4>
        {(permissions?.includes('CreateModuleInstructor') ||
          role === 'SuperAdmin') && (
          <button
            type="button"
            className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer"
            onClick={() => setModalOpen(!modalOpen)}
          >
            Create FAQ
          </button>
        )}
      </div>
      <div className="mt-4">
        {isLoading ? (
          <div className="w-full flex align-middle justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <>
            {moduleFAQs.map((moduleFAQ) => (
              <Accordion key={moduleFAQ.id}>
                <AccordionSummary
                  expandIcon={<ArrowDownwardIcon />}
                  aria-controls="panel1-content"
                  className="bg-gray-100"
                  id="panel1-header"
                >
                  {moduleFAQ.question}
                </AccordionSummary>
                <AccordionDetails>{moduleFAQ.answer}</AccordionDetails>
                {(permissions?.includes('CreateModuleInstructor') ||
                  role === 'SuperAdmin') && (
                  <AccordionActions>
                    <button
                      type="button"
                      onClick={() => onEditFAQBtnClickHandler(moduleFAQ)}
                      className={`bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center `}
                    >
                      <EditIcon fontSize="inherit" />
                      <p>Edit</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteFAQBtnClickHandler(moduleFAQ)}
                      className={`bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center `}
                    >
                      <DeleteIcon fontSize="inherit" />
                      <p>Delete</p>
                    </button>
                  </AccordionActions>
                )}
              </Accordion>
            ))}
          </>
        )}
      </div>
      <Modal
        modalOpen={modalOpen}
        toggleModal={() => onCloseFAQModalHandler()}
        title={`${selectedFaq ? 'Edit' : 'Create'} FAQ for ${moduleName}`}
      >
        {selectedFaq ? (
          <ModuleFAQForm
            toggleModal={() => onCloseFAQModalHandler()}
            faq={selectedFaq}
          />
        ) : (
          <ModuleFAQForm toggleModal={() => onCloseFAQModalHandler()} />
        )}
      </Modal>
      <Modal title="Remove FAQ" modalOpen={deleteModalOpen}>
        <p>
          Are you sure you want to remove{' '}
          <span className="font-medium">{selectedFaq?.question}</span> from this
          module??
        </p>
        {deletingFAQ ? (
          <div className="w-full flex align-middle justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div className=" flex justify-between mt-10">
            <button
              className={`border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center `}
              type="button"
              onClick={() => setDeleteModalOpen(!deleteModalOpen)}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className={`bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center `}
              type="button"
              onClick={() => onDeleteFAQClickHandler()}
            >
              <DeleteIcon fontSize="inherit" />
              <p>Remove FAQ</p>
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ModuleFAQs;
